import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Tv2,
  Tv,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Radio,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Search,
  PieChart,
  Megaphone,
} from "lucide-react";

export const Route = createFileRoute("/services_/television")({
  head: () => ({
    meta: [
      {
        title:
          "Television Advertising Services — KMWAdvertising · Reach Millions & Build Trust",
      },
      {
        name: "description",
        content:
          "Television Advertising Services That Maximise Brand Reach, Awareness & Business Growth. TV Media Planning, Buying, Prime-Time & Sports Advertising by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Television Advertising Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "End-to-end TV advertising solutions: Prime-time spots, sports sponsorships, national & regional buying, and GRP frequency planning.",
      },
    ],
  }),
  component: TelevisionServicePage,
});

export function TelevisionServicePage() {
  const mediaPlanningFactors = [
    {
      title: "Audience Analysis",
      subtitle: "Understanding Viewer Behaviour",
      desc: "Understanding viewer behaviour is fundamental to television planning. We evaluate demographics, age groups, gender distribution, household income, geographic locations, language preferences, viewing habits, lifestyle characteristics, and purchase behaviour to align channels with your target audience.",
      icon: Users,
    },
    {
      title: "Channel Selection",
      subtitle: "Strategic Broadcast Networks",
      desc: "Choosing the right television channels significantly impacts campaign performance. Depending on your objectives, we recommend placements across National entertainment, Regional language, News, Sports, Kids, Lifestyle, Music, Movie, Business, and Infotainment networks.",
      icon: Tv,
    },
    {
      title: "Programme Selection",
      subtitle: "High-Affinity Programming",
      desc: "Advertising during the right programmes enhances viewer engagement. We identify opportunities within Prime-time serials, Live sporting events, Reality shows, Award ceremonies, Morning & Business news, Weekend entertainment, Children's programming, and Festival broadcasts.",
      icon: Sparkles,
    },
    {
      title: "Frequency & Reach Planning",
      subtitle: "Optimised Exposure & Efficiency",
      desc: "Determining how often your advertisement appears is essential for building awareness without overspending. Our planners optimise Gross Rating Points (GRPs), Reach, Frequency, Effective Frequency, Cost Per Rating Point (CPRP), Audience Duplication, and Scheduling Efficiency.",
      icon: BarChart3,
    },
  ];

  const tvBuyingIncludes = [
    "National television buying",
    "Regional television buying",
    "Spot buying",
    "Sponsorship packages",
    "Fixed position placements",
    "Run-of-network campaigns",
    "Burst campaigns",
    "Roadblock advertising",
    "Seasonal campaigns",
    "Festival campaigns",
  ];

  const costOptimisationPoints = [
    "Competitive negotiations with network owners",
    "Efficient inventory selection",
    "Optimised scheduling across dayparts",
    "Bonus spot opportunities & rate leverage",
    "Value-added broadcast integrations",
    "Audience-based buying strategies",
  ];

  const primeTimeIdealFor = [
    "National brand launches",
    "New product introductions",
    "Festive campaigns",
    "Corporate branding initiatives",
    "Automobile launches",
    "FMCG product promotions",
    "Financial services",
    "Consumer electronics",
    "Healthcare awareness campaigns",
  ];

  const sportsCoverage = [
    "Cricket tournaments",
    "Football leagues",
    "Tennis championships",
    "Kabaddi leagues",
    "Formula racing",
    "Olympic events",
    "Regional sporting competitions",
    "International sporting events",
  ];

  const sportsSolutions = [
    "Commercial spot buying",
    "Match sponsorships",
    "Tournament sponsorships",
    "On-screen branding",
    "Broadcast integrations",
    "Pre-match sponsorships",
    "Half-time advertising",
    "Post-match branding",
  ];

  const executionSteps = [
    {
      num: "01",
      title: "Audience Research",
      desc: "Identifying target demographics, media consumption habits, and geographic priorities.",
      icon: Search,
    },
    {
      num: "02",
      title: "Media Strategy",
      desc: "Developing a tailored television plan aligned with campaign objectives and budget.",
      icon: Layers,
    },
    {
      num: "03",
      title: "Creative Coordination",
      desc: "Collaborating with production teams and creative agencies to ensure commercials meet broadcaster specifications and effectively communicate the brand message.",
      icon: Megaphone,
    },
    {
      num: "04",
      title: "Media Buying",
      desc: "Negotiating placements and securing premium inventory across selected channels and programmes.",
      icon: Target,
    },
    {
      num: "05",
      title: "Campaign Monitoring",
      desc: "Tracking advertisement scheduling, ensuring accurate airing, and resolving any delivery issues promptly.",
      icon: Clock,
    },
    {
      num: "06",
      title: "Performance Reporting",
      desc: "Providing transparent reporting on campaign delivery, reach estimates, and optimisation recommendations.",
      icon: FileCheck,
    },
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-orange)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-purple)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-orange)] font-semibold">Television Advertising</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Tv2 className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                Traditional Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Television Advertising Services That Maximise Brand Reach, Awareness & Business Growth
              </h1>

              <p className="mt-6 text-lg sm:text-xl font-accent italic text-[var(--glow-orange)] font-medium border-l-2 border-[var(--glow-orange)] pl-4 py-1">
                Reach Millions. Build Trust. Create Lasting Brand Impact.
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Despite the rapid evolution of digital marketing, television advertising continues to be one of the most influential and trusted advertising mediums for brands looking to achieve mass awareness, establish credibility and drive measurable business growth.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Television remains a powerful storytelling platform capable of delivering emotional engagement, broad demographic reach and unmatched brand visibility. Whether it's a national product launch, regional retail promotion, government awareness campaign or festive brand-building initiative, television advertising creates memorable experiences that influence purchasing decisions at scale.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWA LLP</strong>, we specialise in television media planning, media buying, campaign strategy, sponsorship integrations and commercial execution across leading national, regional and international television networks. With more than two decades of advertising expertise, we help businesses identify the right audience, select the most effective channels, negotiate competitive media rates and optimise every advertising investment for maximum return.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request TV Campaign Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* OVERVIEW & SERVICES INTRO */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                End-to-End Execution
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Our Television Advertising Services
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                KMWA LLP provides end-to-end television advertising solutions, from strategic planning and audience research to media buying, execution and performance analysis. Our services are tailored to businesses of all sizes, including startups, SMEs, multinational corporations and government organisations.
              </p>
            </div>

            {/* TV MEDIA PLANNING GRID */}
            <div className="mb-16">
              <div className="border-b border-white/10 pb-4 mb-8">
                <h3 className="font-display text-xl sm:text-2xl font-bold text-white flex items-center gap-2.5">
                  <BarChart3 className="h-5 w-5 text-[var(--glow-orange)]" />
                  Television Media Planning
                </h3>
                <p className="text-xs sm:text-sm font-mono text-muted-foreground uppercase tracking-wider mt-1">
                  Strategic Planning for Maximum Audience Reach
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {mediaPlanningFactors.map((factor, idx) => {
                  const FIcon = factor.icon;
                  return (
                    <div
                      key={idx}
                      className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)]">
                            FACTOR 0{idx + 1}
                          </span>
                          <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                            <FIcon className="h-5 w-5" />
                          </div>
                        </div>
                        <h4 className="font-display text-xl font-bold text-white mb-1">
                          {factor.title}
                        </h4>
                        <p className="text-xs font-mono text-white/70 mb-3">
                          {factor.subtitle}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          {factor.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* TV MEDIA BUYING & COST OPTIMISATION */}
            <div className="rounded-3xl glass-strong border border-white/15 p-6 sm:p-10 md:p-12 relative overflow-hidden shadow-2xl">
              <div className="glow-orb -top-32 -right-32 h-96 w-96 rounded-full bg-[var(--glow-orange)] opacity-15" />

              <div className="grid lg:grid-cols-12 gap-8 items-start relative z-10">
                <div className="lg:col-span-6 space-y-4">
                  <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block">
                    Broadcast Negotiation
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                    Television Media Buying
                  </h3>
                  <p className="text-sm font-accent italic text-white/90 border-l-2 border-[var(--glow-orange)] pl-3">
                    Secure Premium Placements at Competitive Rates
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Media buying is more than simply purchasing advertising slots. Effective media buying requires market expertise, negotiation skills, long-standing relationships with broadcasters and continuous optimisation.
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Our television media buying specialists negotiate directly with broadcasters, television networks and media owners to secure the most valuable inventory at competitive rates.
                  </p>

                  <div className="pt-4">
                    <h5 className="text-xs font-mono uppercase tracking-wider text-white mb-3">
                      Our TV Media Buying Services Include:
                    </h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {tvBuyingIncludes.map((item, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 p-2.5 rounded-xl bg-white/[0.04] border border-white/5 text-xs text-white/90"
                        >
                          <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-6 bg-[#06060c]/90 rounded-2xl p-6 border border-white/10 space-y-4">
                  <h4 className="text-base font-bold text-white flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-[var(--glow-orange)]" />
                    Cost Optimisation Strategies
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Our buying team focuses on maximising media value through rigorous inventory selection and strategic broadcaster leverage:
                  </p>

                  <div className="space-y-2.5 pt-2">
                    {costOptimisationPoints.map((point, pIdx) => (
                      <div
                        key={pIdx}
                        className="flex items-start gap-2.5 p-3 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90"
                      >
                        <ShieldCheck className="h-4 w-4 text-[var(--glow-orange)] shrink-0 mt-0.5" />
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <p className="text-xs text-muted-foreground italic">
                      Every campaign is monitored to ensure delivery accuracy, zero slot drops, and full contractual compliance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PREMIUM SOLUTIONS: PRIME-TIME & SPORTS ADVERTISING */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                High-Impact Inventory
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Premium Television Advertising Solutions
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* PRIME-TIME TV ADVERTISING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/10 relative overflow-hidden flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-orange)] bg-[var(--glow-orange)]/10 border border-[var(--glow-orange)]/30 mb-4">
                    <Clock className="h-3.5 w-3.5" />
                    Prime-Time Focus
                  </div>

                  <h3 className="font-display text-2xl font-bold text-white">
                    Prime-Time Television Advertising
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Reach Audiences When Attention Is at Its Peak
                  </p>

                  <p className="mt-4 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Prime-time television remains one of the most sought-after advertising opportunities for brands looking to maximise visibility and impact. These high-viewership time slots attract families, working professionals, and decision-makers who are actively engaged with premium entertainment, news, and sports programming.
                  </p>

                  <p className="mt-3 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    At Kris Mediaworks Advertising LLP, we help brands secure strategic prime-time placements that align with campaign objectives and audience behaviour. Our planning process evaluates programme ratings, audience demographics, historical performance, and seasonal viewing trends to recommend the most effective inventory.
                  </p>

                  <div className="pt-6">
                    <span className="text-xs font-mono uppercase tracking-widest text-white/80 block mb-3">
                      Prime-time advertising is ideal for:
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      {primeTimeIdealFor.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 text-xs text-muted-foreground"
                        >
                          <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                          <span className="truncate">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* SPORTS TV ADVERTISING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/10 relative overflow-hidden flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-amber-400 bg-amber-500/10 border border-amber-500/30 mb-4">
                    <Award className="h-3.5 w-3.5" />
                    Live Sports Broadcasters
                  </div>

                  <h3 className="font-display text-2xl font-bold text-white">
                    Sports Television Advertising
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Capture High-Engagement Audiences During Live Events
                  </p>

                  <p className="mt-4 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Live sports continue to attract some of the largest television audiences worldwide. Sporting events create shared experiences, strong emotional investment and exceptionally high viewer attention, making them ideal environments for impactful advertising.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-6 pt-6">
                    <div>
                      <span className="text-xs font-mono uppercase tracking-widest text-white/80 block mb-2">
                        Event Coverage:
                      </span>
                      <div className="space-y-1.5">
                        {sportsCoverage.map((sc, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Check className="h-3 w-3 text-amber-400 shrink-0" />
                            <span>{sc}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <span className="text-xs font-mono uppercase tracking-widest text-white/80 block mb-2">
                        Sports Solutions:
                      </span>
                      <div className="space-y-1.5">
                        {sportsSolutions.map((ss, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Check className="h-3 w-3 text-amber-400 shrink-0" />
                            <span>{ss}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CAMPAIGN EXECUTION PROCESS */}
        <section className="py-16 md:py-24 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                Flawless Execution
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Television Campaign Execution
              </h2>
              <p className="text-sm font-accent italic text-white/90 mt-2">
                From Strategy to Broadcast
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {executionSteps.map((step, idx) => {
                const StepIcon = step.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="font-mono text-2xl font-bold text-white/30 group-hover:text-[var(--glow-orange)] transition-colors">
                          {step.num}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <StepIcon className="h-5 w-5 text-[var(--glow-orange)]" />
                        </div>
                      </div>
                      <h4 className="font-display text-lg font-bold text-white mb-2">
                        {step.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-orange)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Tv2 className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
              Television Media Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Scale Your Brand with Television Advertising?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Connect with KMWA LLP's television media buying and planning specialists to build high-impact broadcast campaigns tailored to your target audience.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request TV Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
